import AgencyHelper from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import FleetHelper from 'features/Fleet/fleetHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTBSButton, {
    KTBSButtonColor,
    KTBSButtonSize,
} from 'general/components/OtherKeenComponents/KTBSButton';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';
import AppColors from 'general/constants/AppColors';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import { useEkyc } from 'hooks/useEKYC';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ShowMoreText from 'react-show-more-text';

const FleetUIHelper = {

    // MARK: ---- CARDS ----
    buildTableInfo: (title, content) => {
        const filteredContent = content?.filter((item) => !item.hide);

        const TableRow = ({ item, isLast }) => {
            const textRef = useRef(null);
            const [isOverflowing, setIsOverflowing] = useState(false);

            useEffect(() => {
                const element = textRef.current;
                if (element) {
                    setIsOverflowing(element.scrollWidth > element.clientWidth);
                }
            }, [item?.value]);

            const textStyles = {
                color: item?.color || AppColors.gray[800],
                fontWeight: 600,
                fontSize: Utils.returnFontSize(13),
                cursor: item?.action ? 'pointer' : 'default',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
            };

            const TextContent = (
                <div
                    ref={textRef}
                    className="font-weight-bolder text-right"
                    style={textStyles}
                    onClick={item?.action ?? (() => { })}
                >
                    <span>{item?.value || ''}</span>
                </div>
            );

            return (
                <>
                    {!item?.notShow && (
                        <div
                            className="d-flex justify-content-between p-3"
                            style={{
                                borderBottom: isLast ? 'none' : `1px dashed ${AppColors.gray[300]}`,
                            }}
                        >
                            <div
                                className="mr-3"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: 500,
                                    fontSize: Utils.returnFontSize(13),
                                }}
                            >
                                {item?.name}
                            </div>
                            <div
                                className="d-flex gap-2 align-items-center"
                                style={{
                                    cursor: item?.action ? 'pointer' : 'default',
                                    overflow: 'hidden',
                                    flex: 1,
                                }}
                            >
                                <div style={{ overflow: 'hidden', flex: 1 }}>
                                    {isOverflowing ? (
                                        <KTTooltip offset={[0, 0]} text={item?.value}>
                                            {TextContent}
                                        </KTTooltip>
                                    ) : (
                                        TextContent
                                    )}
                                </div>
                                {item?.isCopy && (
                                    <i
                                        className="fa-regular fa-copy cursor-pointer"
                                        style={{
                                            width: 16,
                                            height: 18,
                                            color: AppColors.gray[800],
                                            flexShrink: 0,
                                        }}
                                        onClick={() =>
                                            Utils.copyTextToClipboard(item?.value, () =>
                                                ToastHelper.showSuccess('Đã sao chép')
                                            )
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </>
            );
        };

        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {title}
                </div>
                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    {filteredContent?.map((item, index) =>
                        !item?.hide ? (
                            <TableRow key={index} item={item} isLast={index === filteredContent.length - 1} />
                        ) : null
                    )}
                </div>
            </div>
        );
    },

    buildAccountInfoCard: (fleetDetail, isFleetIndividual, adminRole) => {
        return (
            <div>
                {FleetUIHelper.buildTableInfo(t('Thông tin cơ bản'), [
                    {
                        name: isFleetIndividual ? t('Họ và tên') : t('Tên tổ chức/doanh nghiệp'),
                        value: fleetDetail?.name,
                        color: AppColors.gray[800],
                    },
                    {
                        name: t('Mã số thuế'),
                        value: fleetDetail?.taxCode,
                        color: AppColors.gray[800],
                        hide: isFleetIndividual,
                    },
                    {
                        name: t('Số điện thoại'),
                        value: fleetDetail?.phone,
                        color: AppColors.gray[800],
                    },
                    { name: t('Email'), value: fleetDetail?.email, color: AppColors.gray[800] },
                    { name: t('Loại đối tác'), value: t(`accountType_${fleetDetail?.type}`), color: AppColors.gray[800] },
                    {
                        name: t(`Đối tượng quản lý`),
                        value: fleetDetail?.parentAgency?.name,
                        color: adminRole ? AppColors.primary : AppColors.gray[800],
                        action: adminRole
                            ? () => {
                                DealerHelper.navigateToParentAgency(fleetDetail?.parentAgency);
                            }
                            : null,
                    },
                ])}
            </div>
        );
    },

    buildEKYCInfoCard: (fleetDetail, onDone) => {
        const { startEkyc } = useEkyc();
        return (
            <div className="d-flex flex-column gap-1">
                <div
                    className="font-weight-bolder"
                    style={{
                        color: AppColors.gray[900],
                        fontSize: Utils.returnFontSize(14),
                    }}
                >
                    {t('Thông tin định danh eKYC')}
                </div>

                <div className="rounded" style={{ border: `1px solid ${AppColors.gray[300]}` }}>
                    <div
                        className="d-flex justify-content-between p-3"
                        style={{
                            borderBottom: !fleetDetail?.ekycVerified ? '' : `1px dashed ${AppColors.gray[300]}`,
                        }}
                    >
                        <div
                            className="font-weight-bold"
                            style={{
                                color: AppColors.gray[600],
                                fontWeight: '500',
                                fontSize: Utils.returnFontSize(13),
                            }}
                        >
                            {t('status')}
                        </div>
                        <div
                            className="font-weight-bolder"
                            style={{
                                color: AppColors.gray[800],
                                fontWeight: '600',
                                fontSize: Utils.returnFontSize(14),
                            }}
                            onClick={() => { }}
                        >
                            <KTLabel
                                text={fleetDetail?.ekycVerified ? t(`Đã xác thực`) : t(`Chưa xác thực`)}
                                isInline={true}
                                size={KTLabelSize.medium}
                                additionalClassName={`label-light-${fleetDetail?.ekycVerified ? 'success' : 'danger'
                                    }`}
                            />
                        </div>
                    </div>
                    {fleetDetail?.ekycVerified ? (
                        <div className="d-flex justify-content-between pt-3 mx-0 mb-3 px-3">
                            <div
                                className="w-120px font-weight-bold"
                                style={{
                                    color: AppColors.gray[600],
                                    fontWeight: '400',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                            >
                                {t('Dữ liệu xác thực')}
                            </div>
                            <div
                                className="font-weight-bolder cursor-pointer"
                                style={{
                                    color: AppColors.primary,
                                    fontWeight: '600',
                                    fontSize: Utils.returnFontSize(14),
                                }}
                                onClick={() => {
                                    if (fleetDetail?.idCardDocuments?.length)
                                        DealerHelper.handleShowFile(fleetDetail?.idCardDocuments);
                                }}
                            >
                                {fleetDetail?.idCardDocuments?.length
                                    ? `Xem tệp đính kèm (${fleetDetail?.idCardDocuments?.length})`
                                    : ''}
                            </div>
                        </div>
                    ) : (
                        <div className="px-3 mb-3">
                            <KTBSButton
                                iconElement={
                                    <i className="fa-regular fa-shield-check pr-0" style={{ fontSize: 15 }} />
                                }
                                color={KTBSButtonColor.secondary}
                                title={t('Xác thực')}
                                size={KTBSButtonSize.large}
                                borderRadius={5}
                                buttonTitleFontWeight="600"
                                buttonTitleFontSize={13}
                                onPress={() => {
                                    startEkyc(`Agency|${fleetDetail?.agencyId}`, () => onDone());
                                }}
                                buttonTitleColor={AppColors.gray[800]}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    },

    buildDocumentInfoCard: (fleetDetail, isFleetIndividual) => {
        const idCardDocuments = fleetDetail?.ekycVerified === 1
            ? fleetDetail?.idCardDocuments?.slice(1) ?? []
            : fleetDetail?.idCardDocuments ?? []
        return (
            <div>
                {isFleetIndividual
                    ? FleetUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Loại giấy tờ định danh'),
                            value: isFleetIndividual
                                ? fleetDetail?.idCardType === 'CC'
                                    ? 'CCCD'
                                    : 'Hộ chiếu'
                                : 'Giấy phép kinh doanh',
                        },
                        {
                            name: isFleetIndividual
                                ? fleetDetail?.idCardType === 'CC'
                                    ? 'Số CCCD'
                                    : 'Số hộ chiếu'
                                : 'Số giấy phép kinh doanh',
                            value: fleetDetail?.idCardNumber,
                            hide: !isFleetIndividual,
                        },
                        {
                            name: t('Ngày cấp'),
                            value: Utils.formatDateTimeToString(fleetDetail?.idCardIssueDate, 'DD/MM/YYYY'),
                        },
                        {
                            name: t('Nơi cấp'),
                            value: fleetDetail?.idCardPlaceOfIssue,
                            hide: fleetDetail?.idCardType !== 'CC',
                        },
                        {
                            name: t('Hình ảnh giấy tờ'),
                            value: idCardDocuments?.length
                                ? `Xem tệp đính kèm (${idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (idCardDocuments?.length)
                                    DealerHelper.handleShowFile(idCardDocuments);
                            },
                        },
                    ])
                    : FleetUIHelper.buildTableInfo(t('Thông tin giấy tờ'), [
                        {
                            name: t('Giấy phép ĐKKD'),
                            value: fleetDetail?.idCardDocuments?.length
                                ? `Xem tệp đính kèm (${fleetDetail?.idCardDocuments?.length})`
                                : '',
                            color: AppColors.primary,
                            action: () => {
                                if (fleetDetail?.idCardDocuments?.length)
                                    DealerHelper.handleShowFile(fleetDetail?.idCardDocuments);
                            },
                        },
                    ])}
            </div>
        );
    },

    buildOtherInfoCard: (fleetDetail, fleetParentArea, fleetArea, fleetBank, isFleetIndividual, isAdminRole, isFleetInfo) => {
        return (
            <div>
                {FleetUIHelper.buildTableInfo(t('Thông tin khác'), [
                    {
                        name: t('Người đại diện pháp luật'),
                        value: fleetDetail?.legalRepresentative,
                        color: AppColors.gray[800],
                        hide: isFleetIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: fleetDetail?.gender ? t(`gender_${fleetDetail?.gender}`) : '',
                        hide: isFleetIndividual,
                    },
                    {
                        name: t('Chức vụ người đại diện'),
                        value: fleetDetail?.legalPosition,
                        color: AppColors.gray[800],
                        hide: isFleetIndividual,
                    },
                    {
                        name: t('Quốc gia'),
                        value: AgencyHelper.Data.CountryOptions?.find(country => country?.value === fleetDetail?.country)?.name,
                    },
                    {
                        name: t('Tỉnh/Thành phố'),
                        value: fleetParentArea?.name,
                    },
                    { name: t('Phường/Xã'), value: fleetArea?.name },
                    { name: t('Địa chỉ'), value: fleetDetail?.address },
                    {
                        name: t('Ngày sinh'),
                        value: fleetDetail?.dob ? Utils.formatDateTimeToString(fleetDetail?.dob, 'DD/MM/YYYY') : '',
                        hide: !isFleetIndividual,
                    },
                    {
                        name: t('Giới tính'),
                        value: fleetDetail?.gender ? t(`gender_${fleetDetail?.gender}`) : '',
                        hide: !isFleetIndividual,
                    },
                    {
                        name: t('Số tài khoản ngân hàng'),
                        value: fleetBank?.bankAccount,
                        hide: isAdminRole || isFleetInfo,
                    },
                    {
                        name: t('Ngân hàng'),
                        value: fleetBank?.bankName,
                        hide: isAdminRole || isFleetInfo,
                    },
                ])}
            </div>
        );
    },

    buildHsmAgreementCard: (fleetDetail) => {
        return (
            <div>
                {FleetUIHelper.buildTableInfo(t('Thông tin chữ ký số'), [
                    {
                        name:
                            <div className='d-flex gap-1 cursor-pointer'>
                                <span>Số thỏa thuận (Agreement UUID)</span>
                                <span>
                                    <KTTooltip
                                        text={`<div><span>Dùng để kích hoạt tính năng ký số ứng với hợp đồng và các văn bản điện tử.</span></div>`}
                                        textHeader="Số thỏa thuận (Agreement UUID)"
                                        isHtmlContent
                                    >
                                        <i
                                            className="fa-regular fa-info-circle"
                                            style={{ color: AppColors.info }}
                                        />
                                    </KTTooltip>
                                </span>
                            </div>,
                        value: fleetDetail?.hsmAgreementUUID,
                    },
                ])}
            </div>
        );
    },

    buildWorkAddressCard: (fleetDetail, fleetParentArea, fleetArea) => {
        return (
            <div>
                {FleetUIHelper.buildTableInfo(t('Địa chỉ đăng ký làm việc'), [
                    {
                        name: t('Quốc gia'),
                        value: AgencyHelper.Data.CountryOptions?.find(country => country?.value === fleetDetail?.country)?.name,
                    },
                    {
                        name: t('Tỉnh/Thành phố'),
                        value: fleetParentArea?.name,
                    },
                    { name: t('Phường/Xã'), value: fleetArea?.name },
                    { name: t('Địa chỉ'), value: fleetDetail?.workAddress?.workAddress },
                    {
                        name: t('Tọa độ'),
                        value: `${fleetDetail?.workAddress?.latitude}, ${fleetDetail?.workAddress?.longitude}`,
                    },
                ])}
            </div>
        );
    },

    buildChargePaymentCard: (fleetDetail) => {
        return (
            <div>
                {FleetUIHelper.buildTableInfo(t('Chính sách tính phí sạc'), [
                    {
                        name: t('Đối tượng thanh toán'),
                        value: fleetDetail?.fleetDetail?.paymentMethods
                            ?.map(method =>
                                FleetHelper.Data.paymentMethodOptions.find(item => item.value === method)?.name
                            )
                            ?.filter(Boolean)
                            ?.join(', ') ?? '',
                        color: AppColors.gray[800],
                    },
                    {
                        name: t('Loại giá sạc'),
                        value: FleetHelper.Utils.getChargePricingTypeName(fleetDetail?.fleetDetail?.discountRate ?? ''),
                    },
                    {
                        name: t('Tỷ lệ chiết khấu'),
                        value: fleetDetail?.fleetDetail?.discountRate !== null ? fleetDetail?.fleetDetail?.discountRate * 100 + '%' : '',
                        color: AppColors.gray[800],
                    },
                ])}
            </div>
        );
    },

    // MARK: ---- DIALOGS ----
    buildShowFileDialog: (fileList, onClosed = () => { }) => {
        return (
            <div
                className="rounded-bottom-lg p-5"
                style={{
                    backgroundColor: AppColors.gray[100],
                }}
            >
                <div
                    className="bg-white rounded"
                    style={{
                        border: `1px solid ${AppColors.gray[300]}`,
                    }}
                >
                    {fileList?.map((document, index) => (
                        <div
                            key={index}
                            className="font-weight-boldest p-3 cursor-pointer"
                            style={{
                                color: AppColors.primary,
                                borderBottom:
                                    index !== fileList?.length - 1 ? `1px dashed ${AppColors.gray[300]}` : '',
                            }}
                            onClick={() => {
                                DealerHelper.handleShowImage(fileList[index], Utils.getFullUrl(document));
                            }}
                        >
                            {fileList[index]}
                        </div>
                    ))}
                </div>
            </div>
        );
    },


    // MARK: ---- BUTTONS ----
    buildToggleSwitch: (isOn, handleToggle) => {
        return (
            <div
                className="position-relative d-inline-block"
                style={{
                    width: 43,
                    height: 26,
                }}
                onClick={() => handleToggle()}
            >
                <div
                    className="rounded-pill"
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: isOn ? AppColors.primary : AppColors.gray[400],
                        transition: 'background-color 0.3s ease',
                        cursor: 'pointer',
                    }}
                />
                <div
                    className="position-absolute rounded-circle"
                    style={{
                        width: 22,
                        height: 22,
                        backgroundColor: 'white',
                        top: 2,
                        left: isOn ? 19 : 2,
                        transition: 'left 0.3s ease',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                    }}
                />
            </div>
        );
    },
};

export default FleetUIHelper;
